import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { CardData, SavedCardsResponse } from "@models/credit-card";
import {
  ConfirmPaymentResponse,
  InstallmentOptionsResponse,
  PaymentResponse,
  SellerCashSaleResponse,
  SellerChargePayload,
  SellerPaymentLinkResponse,
  SellerPaymentRequest,
  SellerPixChargeResponse,
} from "@models/payment";
import { Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { AttributionContextService } from "./marketing/attribution-context.service";

@Injectable({
  providedIn: "root",
})
export class PaymentService {
  private apiUrl = environment.API_BACKEND;
  private readonly marketingContext = inject(AttributionContextService);

  constructor(private http: HttpClient) {}

  /**
   * Criar pagamento no backend usando PagarMe
   */
  createPayment(
    paymentMethod:
      | "credit_card"
      | "pix"
      | "pix_credit_card"
      | "two_credit_cards",
    options?: {
      cardId?: string;
      cardData?: CardData;
      installments?: number;
      /** Multimeios Pix+cartão: valor do Pix em centavos. */
      pixAmountCents?: number;
      /** Multimeios 2 cartões: valor do 1º cartão em centavos. */
      firstCardAmountCents?: number;
      secondCardId?: string;
      secondCardData?: CardData;
    },
  ): Observable<PaymentResponse> {
    const headers = this.getAuthHeaders();

    const body: any = {
      payment_method: paymentMethod,
      ...this.marketingContext.paymentParameters(),
    };

    // Se tiver card_id, usar cartão salvo
    if (options?.cardId) {
      body.card_id = options.cardId;
    }

    // Se tiver cardData, enviar dados do cartão novo
    if (options?.cardData) {
      body.card_number = options.cardData.card_number;
      body.card_holder_name = options.cardData.card_holder_name;
      body.card_expiration_date = options.cardData.card_expiration_date;
      body.card_cvv = options.cardData.card_cvv;
    }

    if (options?.installments) {
      body.installments = options.installments;
    }

    if (options?.pixAmountCents != null) {
      body.pix_amount_cents = options.pixAmountCents;
    }

    if (options?.firstCardAmountCents != null) {
      body.first_card_amount_cents = options.firstCardAmountCents;
    }

    if (options?.secondCardId) {
      body.second_card_id = options.secondCardId;
    }

    if (options?.secondCardData) {
      body.second_card_number = options.secondCardData.card_number;
      body.second_card_holder_name = options.secondCardData.card_holder_name;
      body.second_card_expiration_date =
        options.secondCardData.card_expiration_date;
      body.second_card_cvv = options.secondCardData.card_cvv;
    }

    return this.http
      .post<PaymentResponse>(`${this.apiUrl}/pagarme/create_payment`, body, {
        headers,
      })
      .pipe(
        map((response) => this.applyPixMockIfNeeded(response, paymentMethod)),
      );
  }

  /**
   * Multimeios parcial: cobrar o valor restante com outro cartão.
   */
  payRemaining(
    orderId: number,
    options?: {
      cardId?: string;
      cardData?: CardData;
    },
  ): Observable<PaymentResponse> {
    const headers = this.getAuthHeaders();
    const body: any = {
      order_id: orderId,
      ...this.marketingContext.paymentParameters(),
    };

    if (options?.cardId) {
      body.card_id = options.cardId;
    }
    if (options?.cardData) {
      body.card_number = options.cardData.card_number;
      body.card_holder_name = options.cardData.card_holder_name;
      body.card_expiration_date = options.cardData.card_expiration_date;
      body.card_cvv = options.cardData.card_cvv;
    }

    return this.http.post<PaymentResponse>(
      `${this.apiUrl}/pagarme/pay_remaining`,
      body,
      { headers },
    );
  }

  /**
   * Criar pagamento para pedido existente
   */
  createPaymentForOrder(
    orderId: number,
    paymentMethod: "credit_card" | "pix",
    options?: {
      cardId?: string;
      cardData?: CardData;
      installments?: number;
    },
  ): Observable<PaymentResponse> {
    const headers = this.getAuthHeaders();

    const body: any = {
      order_id: orderId,
      payment_method: paymentMethod,
      ...this.marketingContext.paymentParameters(),
    };

    // Se tiver card_id, usar cartão salvo
    if (options?.cardId) {
      body.card_id = options.cardId;
    }

    // Se tiver cardData, enviar dados do cartão novo
    if (options?.cardData) {
      body.card_number = options.cardData.card_number;
      body.card_holder_name = options.cardData.card_holder_name;
      body.card_expiration_date = options.cardData.card_expiration_date;
      body.card_cvv = options.cardData.card_cvv;
    }

    if (options?.installments) {
      body.installments = options.installments;
    }

    return this.http
      .post<PaymentResponse>(
        `${this.apiUrl}/pagarme/create_payment_for_order`,
        body,
        { headers },
      )
      .pipe(
        map((response) => this.applyPixMockIfNeeded(response, paymentMethod)),
      );
  }

  /**
   * Obter as opções de parcelamento disponíveis para o carrinho atual.
   * A tabela de juros vive no backend (fonte única).
   */
  getInstallmentOptions(): Observable<InstallmentOptionsResponse> {
    const headers = this.getAuthHeaders();

    return this.http.get<InstallmentOptionsResponse>(
      `${this.apiUrl}/pagarme/installment_options`,
      { headers },
    );
  }

  /**
   * Confirmar pagamento com transaction_id do PagarMe
   */
  confirmPayment(
    orderId: number,
    transactionId: string,
  ): Observable<ConfirmPaymentResponse> {
    const headers = this.getAuthHeaders();

    return this.http.post<ConfirmPaymentResponse>(
      `${this.apiUrl}/pagarme/confirm_payment`,
      {
        order_id: orderId,
        transaction_id: transactionId,
      },
      { headers },
    );
  }

  /**
   * Obter cartões salvos do usuário
   */
  getSavedCards(): Observable<SavedCardsResponse> {
    const headers = this.getAuthHeaders();

    return this.http.get<SavedCardsResponse>(
      `${this.apiUrl}/users/saved_cards`,
      { headers },
    );
  }

  /**
   * Salvar um novo cartão
   */
  saveCard(cardData: CardData): Observable<any> {
    const headers = this.getAuthHeaders();

    return this.http.post(
      `${this.apiUrl}/users/saved_cards`,
      {
        card_number: cardData.card_number,
        card_holder_name: cardData.card_holder_name,
        card_expiration_date: cardData.card_expiration_date,
        card_cvv: cardData.card_cvv,
      },
      { headers },
    );
  }

  /**
   * Remover cartão salvo
   */
  deleteCard(cardId: string): Observable<any> {
    const headers = this.getAuthHeaders();

    return this.http.delete(`${this.apiUrl}/users/saved_cards/${cardId}`, {
      headers,
    });
  }

  /**
   * Obter QR Code PIX
   */
  getPixQrCode(orderId: number): Observable<PaymentResponse> {
    const headers = this.getAuthHeaders();

    return this.http.get<PaymentResponse>(
      `${this.apiUrl}/pagarme/pix_qrcode/${orderId}`,
      { headers },
    );
  }

  /**
   * Verificar status do pagamento
   */
  checkPaymentStatus(orderId: number): Observable<ConfirmPaymentResponse> {
    const headers = this.getAuthHeaders();

    return this.http.post<ConfirmPaymentResponse>(
      `${this.apiUrl}/pagarme/confirm_payment`,
      {
        order_id: orderId,
      },
      { headers },
    );
  }

  createSellerPixCharge(
    payload: SellerChargePayload,
  ): Observable<SellerPixChargeResponse> {
    const headers = this.getAuthHeaders();
    return this.http.post<SellerPixChargeResponse>(
      `${this.apiUrl}/seller/create_pix_charge`,
      payload,
      { headers },
    );
  }

  createSellerPaymentLink(
    payload: SellerChargePayload,
  ): Observable<SellerPaymentLinkResponse> {
    const headers = this.getAuthHeaders();
    return this.http.post<SellerPaymentLinkResponse>(
      `${this.apiUrl}/seller/create_payment_link`,
      payload,
      { headers },
    );
  }

  createSellerCashSale(
    payload: SellerChargePayload,
  ): Observable<SellerCashSaleResponse> {
    const headers = this.getAuthHeaders();
    return this.http.post<SellerCashSaleResponse>(
      `${this.apiUrl}/seller/create_cash_sale`,
      payload,
      { headers },
    );
  }

  /**
   * Reconcilia o status da cobrança do vendedor; o backend pode emitir broadcast no WebSocket.
   */
  refreshSellerPaymentRequest(
    sellerPaymentRequestId: number,
  ): Observable<SellerPaymentRequest> {
    const headers = this.getAuthHeaders();
    const url = `${this.apiUrl}/seller/payment_requests/${sellerPaymentRequestId}/refresh`;
    return this.http.post<SellerPaymentRequest>(url, {}, { headers }).pipe(
      tap({
        next: (body) =>
          console.log(
            "[seller/payment_requests/refresh] resposta do backend:",
            body,
          ),
        error: (err) =>
          console.warn("[seller/payment_requests/refresh] erro HTTP:", err),
      }),
    );
  }

  /**
   * Obter headers de autenticação
   */
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem("auth-token");
    return new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    });
  }

  /**
   * Aplicar mock do PIX quando em desenvolvimento (se o backend não retornar pix_data)
   */
  private applyPixMockIfNeeded(
    response: PaymentResponse,
    paymentMethod: string,
  ): PaymentResponse {
    if (
      !environment.production &&
      (paymentMethod === "pix" || paymentMethod === "pix_credit_card") &&
      !response.pix_data?.qr_code
    ) {
      console.warn("🧪 MODO DESENVOLVIMENTO: Usando dados mock do PIX");
      console.log(
        "📋 Backend não retornou pix_data completo, aplicando mock para desenvolvimento",
      );

      const mockPixData = this.generateMockPixData(response);

      return {
        ...response,
        pix_data: mockPixData,
      };
    }

    return response;
  }

  /**
   * Gerar dados mock do PIX para desenvolvimento
   */
  private generateMockPixData(response: PaymentResponse): any {
    // QR Code PIX mock válido (padrão EMV)
    const mockQrCode =
      "00020126580014br.gov.bcb.pix0136mock-dev-yes7tickets-" +
      Math.random().toString(36).substring(7) +
      "52040000530398654" +
      (response.order?.total_amount || "8.51").replace(".", "") +
      "5802BR5913Yes7Tickets6009SaoPaulo62070503***6304";

    return {
      qr_code: mockQrCode,
      qr_code_url:
        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCI+PHRleHQ+TU9DSyBRUiBDT0RF",
      expires_at: new Date(Date.now() + 3600000).toISOString(), // Expira em 1 hora
      amount: parseFloat(response.order?.total_amount || "0") || 8.51,
      status: "waiting_payment",
    };
  }
}
